import store from '@/store'
import i18n from '@/plugins/i18n'
import ObjArrUtils from '@/utils/ObjArrUtils'
import ApiUtils from '@/utils/api/ApiUtils'
import AppUtils from './AppUtils'
// import router from '@/router'

/** @module StoreUtils */

/**
 * Utilities for application
 * @property {Function} storeApiDispatch Utilities for dispatching store action
 * 
 * @author Tin Ley Ter <leyter.tin@orbitmy.com>
 * @since 2022/07/20
 */
const StoreUtils = {
  /**
   * Utilities for dispatching store action
   *
   * @param {String} dispatchAction Store Action to be called on
   * @param {Object} data Data to be passed to action
   * @param {Object} options Options to be passed to action
   * - loading {Boolean}: [Default: `true`] Whether to show loading animation
   * - callback {Function}: [Default: Empty Function] Callback function
   * - errorDialog {Boolean}: [Default: `true`] Whether to show dialog when error happens
   * - errorTitle {String}: [Default: `common.messages.errorTitle`] Default error title
   * - errorMessage {String}: [Default: `common.messages.errorMessage`] Default error message
   * @param {Object} ctx Vue instance
   * @returns {Promise} Store action returned promise
   */
  storeApiDispatch (dispatchAction, data = {}, options = {}, ctx = null) {
    return baseDispatch(
      function (mergedOptions) {
        const module = dispatchAction.split('/')[0]
        if (mergedOptions.clearError) store.commit(module + '/REMOVE_ERRORS')
        return store.dispatch(dispatchAction, data)
      }, options, ctx)
  },
  storeApiDispatchContext (ctx, dispatchAction, data, options) {
    return this.storeApiDispatch(dispatchAction, data, options, ctx)
  },
  apiDispatch (input, options = {}, ctx = null) {
    return baseDispatch(
      function () {
        return ApiUtils.postData({ data: input })
      }, options, ctx)
  },
  apiDispatchContext (ctx, input, options) {
    return this.apiDispatch(input, options, ctx)
  },

  /**
   * @param {string} path
   * @param {any} value
   */
  storeCommit (path, value) {
    store.commit(path, value)
  }

}

function baseDispatch (callback, options = {}, ctx) {
  const defaultOptions = {
    loading: true,
    clearError: true,
    callback: (res) => { },
    errorDialog: true,
    // throwOnError: false,
    errorTitle: i18n.t('common.messages.errorTitle'),
    errorMessage: i18n.t('common.messages.errorMessage'),
    showDebugError: AppUtils.isDebugMode()
  }
  const mergedOptions = ObjArrUtils.merge(defaultOptions, options)

  const load = (mergedOptions.loading ? 1 : 0)
  store.commit('App/ADJUST_APP_LOADING', load)

  return callback(mergedOptions)
    .then(res => {
      mergedOptions.callback(res)
      return res
    })
    .catch((err) => {
      let res
      try {
        res = JSON.parse(err.message)
      } catch (e) {
        // remove on production
        // store.commit('Notification/SET_DIALOG_ERROR', {
        //   title: 'Framework Error',
        //   text: 'Backend response is not a valid JSON. Please contact the administrator.'
        // })
        throw err
      }
      let message = (typeof res === 'object' && res.responseJSON)
        ? res.responseJSON.message
        : res
      const status = res.status ?? 0

      switch (true) {
        case status === 400:
          ctx.$dialog.notify.info('错误操作:' + message)
          throw new Error('错误操作:' + message)
        // for input form
        case status === 422:
          message = ObjArrUtils.isJsonString(res.responseJSON.message)
            ? JSON.parse(res.responseJSON.message)
            : message
          mergedOptions.errorMessage = message['422'] ?? ctx.$t('common.messages.errorInput')
          ctx.$store.commit('Notification/SET_NOTIFY_ERROR', {
            text: mergedOptions.errorMessage
          })
          return
        case status === 401 && !!localStorage.getItem('token'):
          ctx.$store.dispatch('Auth/logout')
          ctx.$router.replace({ name: 'Login', query: { next: ctx.$router.currentRoute.fullPath } })
          ctx.$dialog.notify.info('登入已过期。请重新登录。')
          return
        case status === 401 :
          ctx.$router.replace({ name: 'Login', query: { next: ctx.$router.currentRoute.fullPath } })
          ctx.$dialog.notify.info('请登录以继续。')
          return
        case status === 403:
          ctx.$router.replace({ name: 'Home' })
          ctx.$dialog.notify.info('您无权访问此页面。')
          return
        case res.statusText === 'abort':
          // do nothing
          return
        case status === 0: // Network error
          // TODO: offine page
          store.commit('Notification/SET_DIALOG_INFO', {
            // title: 'Backend Offline',
            // text: 'We are sorry, but the backend system is currently offline. This may cause some features to be unavailable or not work as expected. We are working to restore service as soon as possible. Please try again later.'
            title: '後端系統離線',
            text: '很抱歉，後端系統目前離線。這可能導致某些功能無法使用或無法正常工作。我們正在努力恢復服務。請稍後再試。'
          })
          throw new Error('Backend Offline')
        // show debug error message
        case mergedOptions.showDebugError:
          try {
            ctx.$dialog.error({
              title: mergedOptions.errorTitle,
              text: `[${message}] \n Please check the console for more details.`,
              actions: {
                true: 'OPEN DEBUG URL'
              }
            }).then(ans => {
              if (ans) {
                const targetUrl = res.responseJSON.debug_url
                window.open(targetUrl, '_blank')
              }
            })
          } catch (e) {
            console.error('Error:', res.responseJSON)
          }
          console.error('Error:', res.responseJSON)
          return
        // default error message, for production
        default:
          store.commit('Notification/SET_DIALOG_INFO', {
            // title: mergedOptions.errorTitle,
            // text: mergedOptions.errorMessage
            title: '操作錯誤',
            text: '登入權限或輸入資料不正確，請重就登入或再輸入正確資料。'
          })
          throw err
      }
    })
    .finally(() => {
      store.commit('App/ADJUST_APP_LOADING', -load)
    })
}
export default StoreUtils
